'use client';
import { useCallback, useRef, useState } from 'react';
import { marketEditorialCreateTransport } from '../transport/market-editorial-create.js';
import { runMarketCreation } from './orchestrator.js';
import { INITIAL_PROGRESS_STATE, } from './types.js';
export function useCreateMarket(deps) {
    const [progress, setProgress] = useState(INITIAL_PROGRESS_STATE);
    const [isPending, setIsPending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const controllerRef = useRef(null);
    const reset = useCallback(() => {
        setProgress(INITIAL_PROGRESS_STATE);
        setIsPending(false);
        setResult(null);
        setError(null);
    }, []);
    const cancel = useCallback(() => {
        if (controllerRef.current !== null) {
            controllerRef.current.abort();
            controllerRef.current = null;
        }
    }, []);
    const mutate = useCallback(async (params, callbacks) => {
        // Tear down any prior attempt.
        if (controllerRef.current !== null) {
            controllerRef.current.abort();
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        setIsPending(true);
        setResult(null);
        setError(null);
        setProgress({
            ...INITIAL_PROGRESS_STATE,
            status: 'preparing',
            outcomesTotal: params.outcomes.length,
        });
        try {
            const res = await runMarketCreation({
                connection: deps.connection,
                submitTransaction: deps.submitTransaction,
                activeWallet: deps.activeWallet,
                signingDeps: deps.signingDeps,
                turnkeyDeps: deps.turnkeyDeps,
                params,
                onProgress: setProgress,
                signal: controller.signal,
                editorialCreateTransport: deps.editorialCreateTransport ?? marketEditorialCreateTransport,
                ...(deps.logger !== undefined ? { logger: deps.logger } : {}),
            });
            setResult(res);
            setIsPending(false);
            callbacks?.onSuccess?.(res);
            return res;
        }
        catch (err) {
            const wrapped = err instanceof Error ? err : new Error(String(err));
            setError(wrapped);
            setIsPending(false);
            callbacks?.onError?.(wrapped);
            return undefined;
        }
        finally {
            if (controllerRef.current === controller) {
                controllerRef.current = null;
            }
        }
    }, [
        deps.connection,
        deps.submitTransaction,
        deps.activeWallet,
        deps.signingDeps,
        deps.turnkeyDeps,
        deps.editorialCreateTransport,
        deps.logger,
    ]);
    return { progress, isPending, result, error, mutate, reset, cancel };
}
//# sourceMappingURL=use-create-market.js.map